import React, { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import { CheckCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

// Type alias for compatibility with shadcn/ui toast pattern
export type ToastActionElement = React.ReactElement;

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    const dismissTimer = setTimeout(() => handleClose(), duration);

    return () => {
      clearTimeout(timer);
      clearTimeout(dismissTimer);
    };
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => onClose(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStyles = () =>
    'flex items-start gap-3 p-4 rounded-xl shadow-lg border bg-white transition-all duration-300 transform';

  return (
    <div
      className={
        getStyles() +
        ' ' +
        (isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0') +
        ' ' +
        (isLeaving ? 'translate-x-full opacity-0' : '') +
        ' min-w-[320px] max-w-[420px]'
      }
      role="alert"
      aria-live="assertive"
    >
      <div className="flex-shrink-0">{getIcon()}</div>

      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h4>

        {message && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {message}
          </p>
        )}
      </div>

      <button
        onClick={handleClose}
        className="flex-shrink-0 ml-3 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Close notification"
      >
        <span className="text-lg font-bold">×</span>
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Array<{
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
  }>;
  onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => (
  <div className="fixed bottom-4 right-4 z-[1080] space-y-3 pointer-events-none">
    {toasts.map((toast) => (
      <div key={toast.id} className="pointer-events-auto">
        <Toast {...toast} onClose={onClose} />
      </div>
    ))}
  </div>
);

interface ToastOptions {
  duration?: number;
}

type ToastEventDetail = {
  type?: ToastType;
  title?: string;
  message?: string;
  duration?: number;
};

const useToastInternal = () => {
  const [toasts, setToasts] = useState<
    Array<{ id: string; type: ToastType; title: string; message?: string; duration?: number }>
  >([]);

  const addToast = (type: ToastType, title: string, message?: string, options?: ToastOptions) => {
    const id = Math.random().toString(36).slice(2, 11);
    setToasts((prev) => [...prev, { id, type, title, message, duration: options?.duration }]);
    return id;
  };

  const removeToast = (id: string) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  return {
    toasts,
    success: (t: string, m?: string, o?: ToastOptions) => addToast('success', t, m, o),
    error: (t: string, m?: string, o?: ToastOptions) => addToast('error', t, m, o),
    warning: (t: string, m?: string, o?: ToastOptions) => addToast('warning', t, m, o),
    info: (t: string, m?: string, o?: ToastOptions) => addToast('info', t, m, o),
    removeToast,
    setToasts,
  };
};

interface ToastContextType {
  success: any;
  error: any;
  warning: any;
  info: any;
  dismiss: (id?: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useGlobalToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useGlobalToast must be used within a ToastProvider');
  return ctx;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const { toasts, success, error, warning, info, removeToast, setToasts } = useToastInternal();

  useEffect(() => {
    const handleToastEvent = (event: Event) => {
      const detail = (event as CustomEvent<ToastEventDetail>).detail;
      if (!detail?.title) return;

      const options = { duration: detail.duration };
      switch (detail.type) {
        case 'success':
          success(detail.title, detail.message, options);
          break;
        case 'warning':
          warning(detail.title, detail.message, options);
          break;
        case 'error':
          error(detail.title, detail.message, options);
          break;
        case 'info':
        default:
          info(detail.title, detail.message, options);
      }
    };

    window.addEventListener('app:toast', handleToastEvent);
    return () => {
      window.removeEventListener('app:toast', handleToastEvent);
    };
  }, [error, info, success, warning]);

  return (
    <ToastContext.Provider
      value={{
        success,
        error,
        warning,
        info,
        dismiss: (id?: string) => (id ? removeToast(id) : setToasts([])),
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};

export const ToastViewport = () => null;
export const ToastTitle = ({ children }: { children: ReactNode }) => <div>{children}</div>;
export const ToastDescription = ({ children }: { children: ReactNode }) => <div>{children}</div>;
export const ToastClose = () => null;
export const ToastAction = ({ children }: { children: ReactNode }) => <div>{children}</div>;

export default Toast;
